import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Search, FolderKanban, CreditCard, Plus, KeyRound } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { getAllClients, getProjects, getPayments } from '../../lib/supabase/queries';
import { supabase } from '../../lib/supabase/client';
import type { Profile } from '../../types';

interface ClientWithStats extends Profile {
  projectCount: number;
  activeProjectCount: number;
  totalPaid: number;
  totalOutstanding: number;
}

export function ClientsPage() {
  const [clients, setClients] = useState<ClientWithStats[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Create client modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPassword, setNewClientPassword] = useState('');
  const [newClientCompany, setNewClientCompany] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  // Reset password state
  const [resettingPasswordFor, setResettingPasswordFor] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const [clientsData, projectsData, paymentsData] = await Promise.all([
        getAllClients(),
        getProjects(),
        getPayments(),
      ]);

      // Calculate stats for each client
      const clientsWithStats: ClientWithStats[] = clientsData.map((client) => {
        const clientProjects = projectsData.filter(
          (p) => p.client_id === client.id
        );
        const clientPayments = paymentsData.filter(
          (p) => p.client_id === client.id
        );

        return {
          ...client,
          projectCount: clientProjects.length,
          activeProjectCount: clientProjects.filter(
            (p) => p.status !== 'complete'
          ).length,
          totalPaid: clientPayments
            .filter((p) => p.status === 'paid')
            .reduce((sum, p) => sum + p.amount, 0),
          totalOutstanding: clientPayments
            .filter((p) => p.status === 'pending')
            .reduce((sum, p) => sum + p.amount, 0),
        };
      });

      setClients(clientsWithStats);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setCreateError(null);
    setCreateSuccess(false);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        setCreateError('You must be logged in to create clients');
        return;
      }

      const response = await supabase.functions.invoke('admin-create-user', {
        body: {
          email: newClientEmail,
          password: newClientPassword,
          companyName: newClientCompany,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.error) {
        setCreateError(response.error.message || 'Failed to create client');
        return;
      }

      if (response.data?.error) {
        setCreateError(response.data.error);
        return;
      }

      setCreateSuccess(true);
      setNewClientEmail('');
      setNewClientPassword('');
      setNewClientCompany('');

      // Refresh the clients list
      await fetchData();

      // Close modal after a short delay
      setTimeout(() => {
        setIsCreateModalOpen(false);
        setCreateSuccess(false);
      }, 2000);

    } catch (error: any) {
      setCreateError(error.message || 'An unexpected error occurred');
    } finally {
      setIsCreating(false);
    }
  };

  const handleResetPassword = async (clientId: string) => {
    setResettingPasswordFor(clientId);
    setResetError(null);
    setResetSuccess(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        setResetError('You must be logged in to reset passwords');
        setResettingPasswordFor(null);
        return;
      }

      const response = await supabase.functions.invoke('admin-reset-password', {
        body: { userId: clientId },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.error) {
        setResetError(response.error.message || 'Failed to send reset email');
        return;
      }

      if (response.data?.error) {
        setResetError(response.data.error);
        return;
      }

      setResetSuccess(clientId);
      setTimeout(() => setResetSuccess(null), 3000);

    } catch (error: any) {
      setResetError(error.message || 'An unexpected error occurred');
    } finally {
      setResettingPasswordFor(null);
    }
  };

  const filteredClients = clients.filter((client) =>
    client.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-obsidian dark:text-signal">
            Clients
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            Manage your client accounts
          </p>
        </div>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Create Client
        </Button>
      </div>

      {/* Error/Success messages */}
      {resetError && (
        <div className="p-4 rounded bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400 text-sm">{resetError}</p>
        </div>
      )}

      {/* Search */}
      <div className="max-w-md">
        <Input
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftIcon={<Search className="w-4 h-4" />}
        />
      </div>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <Card variant="bordered" padding="lg">
          <CardContent className="text-center py-12">
            <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-obsidian dark:text-signal">
              No clients found
            </h3>
            <p className="mt-1 text-slate-500 dark:text-slate-400">
              {searchQuery
                ? 'Try adjusting your search'
                : 'Click "Create Client" to add your first client'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <Card key={client.id} variant="bordered" padding="md">
              <CardContent>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-obsidian dark:text-signal">
                      {client.company_name || 'Unnamed Company'}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Since {format(new Date(client.created_at), 'MMM yyyy')}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-amber/10 border border-amber/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-amber">
                      {client.company_name?.charAt(0) || 'C'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <FolderKanban className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {client.projectCount} project
                      {client.projectCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      ${(client.totalPaid / 100).toLocaleString()} paid
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {client.activeProjectCount > 0 && (
                    <Badge variant="info">
                      {client.activeProjectCount} active project
                      {client.activeProjectCount !== 1 ? 's' : ''}
                    </Badge>
                  )}

                  {client.totalOutstanding > 0 && (
                    <Badge variant="warning">
                      ${(client.totalOutstanding / 100).toLocaleString()} outstanding
                    </Badge>
                  )}

                  {resetSuccess === client.id && (
                    <Badge variant="success">Reset email sent!</Badge>
                  )}
                </div>

                <div className="flex gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <Link to="/dashboard/projects" className="flex-1">
                    <Button variant="outline" size="sm" className="w-full">
                      Projects
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResetPassword(client.id)}
                    isLoading={resettingPasswordFor === client.id}
                    leftIcon={<KeyRound className="w-3 h-3" />}
                  >
                    Reset PW
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Client Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setCreateError(null);
          setCreateSuccess(false);
        }}
        title="Create New Client"
      >
        <form onSubmit={handleCreateClient} className="space-y-4">
          {createError && (
            <div className="p-3 rounded bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800">
              <p className="text-red-600 dark:text-red-400 text-sm">{createError}</p>
            </div>
          )}

          {createSuccess && (
            <div className="p-3 rounded bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800">
              <p className="text-green-600 dark:text-green-400 text-sm">
                Client created successfully! They can now log in.
              </p>
            </div>
          )}

          <Input
            label="Company Name"
            type="text"
            value={newClientCompany}
            onChange={(e) => setNewClientCompany(e.target.value)}
            placeholder="Acme Inc"
            required
          />

          <Input
            label="Email Address"
            type="email"
            value={newClientEmail}
            onChange={(e) => setNewClientEmail(e.target.value)}
            placeholder="client@company.com"
            required
          />

          <Input
            label="Temporary Password"
            type="text"
            value={newClientPassword}
            onChange={(e) => setNewClientPassword(e.target.value)}
            placeholder="Enter a temporary password"
            hint="Share this with the client so they can log in and change it"
            required
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCreateModalOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isCreating}
              className="flex-1"
            >
              Create Client
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default ClientsPage;
